import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { getCart, cartTotal, getBranding, clearCart, clearBranding } from "@/lib/cart";
import LogoUpload from "@/components/LogoUpload";

export default function Checkout() {
  const navigate = useNavigate();
  const cart = getCart();
  const [branding, setBranding] = useState(getBranding() || { logo_text: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    event_name: "",
    event_date: "",
    shipping_address: "",
    order_notes: "",
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  if (cart.length === 0) {
    return <p className="py-16 text-center">Your cart is empty.</p>;
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const order = await api.post("/orders", {
        items: cart.map((c) => ({
          product_id: c.product_id,
          name: c.name,
          price: c.price,
          quantity: c.quantity,
        })),
        ...form,
        logo_filename: branding.logo_filename || null,
        logo_text: branding.logo_text || null,
      });
      const checkout = await api.post(
        `/orders/${order.data.order_number}/checkout`,
      );
      clearCart();
      clearBranding();
      // Stripe redirect, or local confirmation when Stripe is mocked.
      window.location.href = checkout.data.url;
    } catch (err) {
      setError(err.response?.data?.detail || "Could not place order.");
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="grid gap-8 md:grid-cols-3">
      <div className="space-y-6 md:col-span-2">
        <section className="card p-6">
          <h2 className="mb-4 text-lg font-bold">Business &amp; event details</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label">Company name *</label>
              <input className="input" required value={form.company_name} onChange={set("company_name")} />
            </div>
            <div>
              <label className="label">Contact name *</label>
              <input className="input" required value={form.contact_name} onChange={set("contact_name")} />
            </div>
            <div>
              <label className="label">Email *</label>
              <input type="email" className="input" required value={form.email} onChange={set("email")} />
            </div>
            <div>
              <label className="label">Phone</label>
              <input className="input" value={form.phone} onChange={set("phone")} />
            </div>
            <div>
              <label className="label">Event / tournament name *</label>
              <input
                className="input"
                required
                placeholder="e.g. Riverbend Charity Classic 2026"
                value={form.event_name}
                onChange={set("event_name")}
              />
            </div>
            <div>
              <label className="label">Event date</label>
              <input type="date" className="input" value={form.event_date} onChange={set("event_date")} />
            </div>
          </div>
          <div className="mt-4">
            <label className="label">Shipping address *</label>
            <textarea className="input" rows="2" required value={form.shipping_address} onChange={set("shipping_address")} />
          </div>
          <div className="mt-4">
            <label className="label">Order notes</label>
            <textarea className="input" rows="2" value={form.order_notes} onChange={set("order_notes")} />
          </div>
        </section>

        <section className="card p-6">
          <h2 className="mb-4 text-lg font-bold">Branding</h2>
          <LogoUpload value={branding} onChange={setBranding} />
        </section>
      </div>

      {/* Order summary */}
      <aside className="card h-fit space-y-3 p-6">
        <h2 className="text-lg font-bold">Order summary</h2>
        {cart.map((c) => (
          <div key={c.product_id} className="flex justify-between text-sm">
            <span>
              {c.name} × {c.quantity}
            </span>
            <span>${(c.price * c.quantity).toFixed(2)}</span>
          </div>
        ))}
        <div className="flex justify-between border-t border-gray-100 pt-3 font-extrabold">
          <span>Total</span>
          <span className="text-fairway-700">${cartTotal().toFixed(2)}</span>
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <button className="btn-primary w-full" disabled={submitting}>
          {submitting ? "Placing order…" : "Place Order & Pay"}
        </button>
      </aside>
    </form>
  );
}
